/**
 * Add Organization Form Composer
 *
 * Form for creating new organizations with complete contact and account information.
 * organizationId is auto-generated on submission.
 *
 * Form metadata and field configurations are loaded from form.yml
 */

import * as React from 'react'
import { useAppForm } from '@/hooks/app-form'
import { useApiMutation, useApiQuery } from '@/api/ApiQueryServiceProvider'
import {
  AddOrganizationFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddOrganizationFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import * as renderFunctions from '../../formRenderFunctions'
import type { ControlConfig } from '../../formRenderFunctions'
import { PhoneInputField } from '@/components/ui/phone-input'
import { CountryDropdown } from '@/components/ui/country-dropdown'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Building } from 'lucide-react'
import { AppLogger } from '@/lib/logger'
import { validateOrgCode } from '@/lib/utils'
import { useToast } from '@/contexts/ToastContext'

const log = AppLogger.getLogger(import.meta)

const {
  renderTextControl,
  renderNumberControl,
  renderButtonControl,
  renderSelectControl,
} = renderFunctions

// ============================================================================
// Component Props
// ============================================================================

export type AddOrganizationComposerProps = {
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues?: Partial<AddOrganizationFormData>
  showHeader?: boolean
  submitLabel?: string
}

// ============================================================================
// Field validation via Zod schema (onBlur only)
// ============================================================================

const validateField = (
  name: keyof AddOrganizationFormData,
  value: unknown,
): string | null => {
  const shape = AddOrganizationFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

// ============================================================================
// Composer Component
// ============================================================================

export function AddOrganizationComposer(props: AddOrganizationComposerProps) {
  const { onSuccess, onError, onCancel, initialValues, showHeader = true, submitLabel } = props
  const { toast } = useToast()

  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const isRequiredField = (name: string) => {
    // type/role are optional (backend only requires name, orgCode, country, emailId).
    return ['name', 'orgCode', 'country', 'emailId'].includes(name)
  }

  // Fetch users for Account Manager dropdown
  const { data: usersData } = useApiQuery('getUsers', {}, {})

  // Fetch existing organizations to guard against a duplicate name/email before submit.
  // NOTE: interim client-side check — duplicate detection ultimately belongs in the
  // backend (UNIQUE name/email_id/website → 409). Kept for now until the backend maps it.
  const { data: orgsData } = useApiQuery('getOrganizations', {}, {})

  const userOptions = React.useMemo(() => {
    const users = usersData?.content?.users
    if (!users || !Array.isArray(users)) return []
    return users.map((user: any) => {
      const email = user.emailAddress || user.emailId || user.email || ''
      return {
        // users are identified by userNumber (there is no `id` field on the
        // getUsers response); accountManagerId is a free-form varchar on the backend
        value: String(user.userNumber),
        label: email
          ? `${user.firstName} ${user.lastName} (${email})`
          : `${user.firstName} ${user.lastName}`,
      }
    })
  }, [usersData])

  const upsertOrganization = useApiMutation('upsertOrganization', 'POST', {
    invalidates: ['getOrganizations'],
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      log.error('Failed to create organization:', error)
      // The backend currently returns a generic 500 ("Internal Server Error") for a
      // duplicate-key violation without naming the field. mint.organizations enforces
      // UNIQUE(name), UNIQUE(email_id) and UNIQUE(website), so surface a helpful hint for
      // the generic case. When the backend returns a meaningful message (e.g. a 409 that
      // names the field), pass that through verbatim instead.
      const raw = (error?.message || '').trim()
      const isGeneric = raw === '' || /internal server error/i.test(raw)
      toast({
        title: 'Could not create organization',
        description: isGeneric
          ? 'An organization with the same name, email, or website may already exist. Please use a different value.'
          : raw,
        type: 'error',
      })
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: AddOrganizationFormSchema,
    defaultValues: {
      name: initialValues?.name ?? '',
      orgCode: initialValues?.orgCode ?? '',
      country: initialValues?.country ?? '',
      type: initialValues?.type ?? '',
      role: initialValues?.role ?? '',
      emailId: initialValues?.emailId ?? '',
      phone: initialValues?.phone ?? '',
      website: initialValues?.website ?? '',
      taxId: initialValues?.taxId ?? '',
      accountManagerId: initialValues?.accountManagerId ?? '',
      employeeCount: initialValues?.employeeCount ?? (undefined as any),
    },
    onSubmit: async ({ value }: { value: any }) => {
      const parsed = AddOrganizationFormSchema.safeParse({
        name: (value.name as string)?.trim() ?? '',
        orgCode: (value.orgCode as string)?.trim() ?? '',
        country: (value.country as string)?.trim() ?? '',
        type: value.type,
        role: value.role,
        emailId: (value.emailId as string)?.trim() ?? '',
        phone: (value.phone as string)?.trim() ?? '',
        website: (value.website as string)?.trim() ?? '',
        taxId: (value.taxId as string)?.trim() ?? '',
        accountManagerId: (value.accountManagerId as string)?.trim() ?? '',
        employeeCount: value.employeeCount
          ? parseInt(value.employeeCount as string, 10)
          : undefined,
      })

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string') {
            form.setFieldMeta(fieldName as any, (meta) => ({
              ...meta,
              errors: [issue.message],
            }))
          }
        }
        return
      }

      // DB stores org_code as varchar(4); enforce the fixed 4-char length before submit.
      const orgCodeError = validateOrgCode(parsed.data.orgCode)
      if (orgCodeError) {
        form.setFieldMeta('orgCode', (meta) => ({ ...meta, isTouched: true, errors: [orgCodeError] }))
        toast({ title: 'Invalid org code', description: orgCodeError, type: 'error' })
        return
      }

      // Interim duplicate guard (until the backend returns a proper 409). mint.organizations
      // enforces UNIQUE(name) AND UNIQUE(email_id); Postgres rejects either with a raw 500,
      // so surface a clear validation message on the offending field instead.
      const existingOrgs =
        ((orgsData as any)?.content?.organizations ?? []) as Array<{ name?: string; emailId?: string; email_id?: string; email?: string }>

      // Duplicate NAME → clear message (DB enforces UNIQUE(name)).
      const nameExists = existingOrgs.some(
        (o) => (o.name ?? '').trim().toLowerCase() === parsed.data.name.trim().toLowerCase(),
      )
      if (nameExists) {
        form.setFieldMeta('name', (meta) => ({
          ...meta,
          isTouched: true,
          errors: ['An organization with this name already exists'],
        }))
        toast({
          title: 'Organization already exists',
          description: `An organization named "${parsed.data.name}" already exists. Please use a different name.`,
          type: 'error',
        })
        return
      }

      // Duplicate EMAIL → clear message (DB enforces UNIQUE(email_id); the backend also
      // upserts by email, so an existing email would silently UPDATE that org).
      const emailExists = existingOrgs.some((o) => {
        const orgEmail = (o.emailId ?? o.email_id ?? o.email ?? '').trim().toLowerCase()
        return orgEmail !== '' && orgEmail === parsed.data.emailId.trim().toLowerCase()
      })
      if (emailExists) {
        form.setFieldMeta('emailId', (meta) => ({
          ...meta,
          isTouched: true,
          errors: ['An organization with this email already exists'],
        }))
        toast({
          title: 'Organization already exists',
          description: `An organization with the email "${parsed.data.emailId}" already exists. Please use a different email.`,
          type: 'error',
        })
        return
      }

      const apiRequest = transformToApiRequest(parsed.data)
      await upsertOrganization.mutateAsync(apiRequest)
    },
  })

  const renderControl = (control: any) => {
    const isRequired = isRequiredField(control.name)

    // Country field — uses reusable CountryDropdown component
    if (control.name === 'country') {
      return (
        <form.AppField
          key={control.name}
          name="country"
          validators={{
            onBlur: ({ value }) => validateField('country', value) || undefined,
          }}
        >
          {(bound) => (
            <CountryDropdown
              label={control.label}
              value={bound.state.value as string}
              onChange={(c) => bound.handleChange(c.name)}
              required={isRequiredField('country')}
              disabled={control.disabled ?? false}
              error={bound.state.meta.errors?.[0]?.toString()}
            />
          )}
        </form.AppField>
      )
    }

    // Phone field — uses reusable PhoneInputField component
    if (control.htmlControl === 'phone') {
      return (
        <form.AppField
          key={control.name}
          name="phone"
          validators={{
            // Validate live (onChange) AND on blur — PhoneInputField emits its value
            // via onChange, so onChange gives immediate feedback for short/invalid
            // numbers (e.g. a single digit) instead of staying silent.
            onChange: ({ value }) => validateField('phone', value) || undefined,
            onBlur: ({ value }) => validateField('phone', value) || undefined,
          }}
        >
          {(bound) => (
            <PhoneInputField
              label={control.label}
              value={bound.state.value as string}
              onChange={(val) => bound.handleChange(val ?? '')}
              onBlur={bound.handleBlur}
              required={isRequired}
              disabled={control.disabled ?? false}
              placeholder={control.placeholder}
              error={bound.state.meta.errors?.[0]?.toString()}
            />
          )}
        </form.AppField>
      )
    }

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) =>
            validateField(control.name as keyof AddOrganizationFormData, value)
            || (control.name === 'orgCode' ? validateOrgCode(value) : null)
            || undefined,
        }}
      >
        {(bound) => {
          const baseConfig: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
          }

          if (control.htmlControl === 'select') {
            if (control.name === 'accountManagerId') {
              return renderSelectControl({ ...baseConfig, options: userOptions }, bound)
            }
            return renderSelectControl({ ...baseConfig, options: control.selectOptions || [] }, bound)
          }

          if (control.htmlControl === 'input' && control.inputType === 'number') {
            return renderNumberControl(baseConfig, bound)
          }

          return renderTextControl(baseConfig, bound)
        }}
      </form.AppField>
    )
  }

  const renderFormContent = () => {
    return groups.map((group) => {
      const groupControls = controlsByGroup.get(group.formGroupName) || []
      if (groupControls.length === 0) return null
      return (
        <div key={group.formGroupName} className="space-y-4">
          {groupControls.map(renderControl)}
        </div>
      )
    })
  }

  const formBody = (
    <>
      {renderFormContent()}

      <div className="pt-4 flex justify-center gap-3">
        {renderButtonControl({
          label: FORM_CONFIG.cancelButton?.label || 'Cancel',
          variant: 'outline',
          onClick: onCancel,
          visibility: !!onCancel,
          type: 'button',
        })}

        {renderButtonControl(
          {
            label: upsertOrganization.isPending
              ? (FORM_CONFIG.submitButton?.loadingLabel || 'Saving...')
              : (submitLabel ?? (FORM_CONFIG.submitButton?.label || 'Submit')),
            variant: 'default',
            type: 'submit',
          },
          { SubmitButton: form.SubmitButton },
        )}
      </div>
    </>
  )

  return (
    <form.AppForm>
      {showHeader ? (
        <div className="space-y-4">
          <Card className="shadow-sm pb-0 overflow-hidden">
            <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Building className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{FORM_CONFIG.formTitle}</CardTitle>
                  <CardDescription>{FORM_CONFIG.formDescription}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="space-y-6 pt-6">{formBody}</CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">{formBody}</div>
      )}
    </form.AppForm>
  )
}

// Re-exports
export {
  AddOrganizationFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddOrganizationFormData,
} from './schema'
